<template>
  <module-template
    id="scrolltainer"
    :title="$t('global.menu.environment-properties')"
    canCreate
    :createToolTip="
      $t('global.header.newFem', [
        $t('global.property.envProperty').toLowerCase(),
      ])
    "
    can-import
    @newItem="addProperty"
    @import="onImport"
  >
    <ooliba-basic-table
      :headers="headers"
      :items="items"
      :loading="busy"
      sort-by="name"
      no-pagination
      :height="tableHeight(85, items?.length)"
    >
      <template #[`item.action`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-2"
              @click="editProperty(item)"
              v-bind="attrs"
              v-on="on"
            >
              pic-edit
            </v-icon>
          </template>
          <span>{{ $t("global.action.edit") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              color="red"
              @click="deleteConfirm(item)"
              v-bind="attrs"
              v-on="on"
            >
              pic-delete
            </v-icon>
          </template>
          <span>{{ $t("global.tooltip.effisoftToolbarDelete") }}</span>
        </v-tooltip>
      </template>
    </ooliba-basic-table>

    <v-dialog v-model="showDeleteConfirm" width="500">
      <confirm-alert
        @cancel="cancelDelete"
        @confirm="deleteProperty"
      ></confirm-alert>
    </v-dialog>
    <v-dialog v-model="showAddEdit" width="500">
      <add-edit-property
        v-if="showAddEdit"
        @done="onAddEditDone"
        @cancel="onAddEditCancel"
        :isEdit="isEdit"
        :initProperty="editingProperty"
      />
    </v-dialog>

    <v-dialog v-model="showImport" width="500">
      <v-overlay absolute :value="uploading">
        <v-chip color="blue" dark>{{ $t("global.msg.importing") }}</v-chip>
      </v-overlay>
      <v-card>
        <v-card-title>{{ $t("global.property.import") }}</v-card-title>
        <v-card-subtitle v-if="overwrites.length > 0">
          {{ $t("global.property.props-overwritten") }}
        </v-card-subtitle>

        <v-form @submit.prevent="onImportConfirm" class="mx-auto">
          <v-card-text>
            <v-file-input
              v-if="overwrites.length === 0"
              show-size
              :label="$t('global.concepts.choose-file')"
              accept=".properties,.txt"
              truncate-length="100"
              v-model="file"
              outlined
            ></v-file-input>

            <ooliba-basic-table
              v-else
              :headers="overwritesHeaders"
              :items="overwrites"
              sort-by="name"
              no-pagination
            >
            </ooliba-basic-table>

            <div v-if="overwrites.length > 0" class="mt-4 font-weight-bold">
              {{ $t("global.action.confirm-continue-sure") }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="onCancel">{{ $t("global.action.cancel") }}</v-btn>
            <v-btn color="primary" :disabled="!file" type="submit">
              {{ $t("global.modules.import") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import { get, remove, postForm } from "@/model/api";
import ConfirmAlert from "@/components/ConfirmAlert";
import AddEditProperty from "@/components/AddEditProperty";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import { tableHeight } from "@/model/util";

export default {
  name: "PropertyList",
  components: {
    ModuleTemplate,
    ConfirmAlert,
    AddEditProperty,
    OolibaBasicTable,
  },
  data() {
    return {
      headers: [
        { text: this.$t("global.concepts.name"), value: "name" },
        { text: this.$t("global.concepts.value"), value: "value" },
        {
          text: this.$t("global.concepts.actions"),
          value: "action",
          sortable: false,
        },
      ],
      items: [],
      isEdit: false,
      editingProperty: {},
      deletingProperty: {},
      showAddEdit: false,
      properties: [],
      showDeleteConfirm: false,

      showImport: false,
      file: undefined,
      uploading: false,
      overwrites: [],
      overwritesHeaders: [
        { text: this.$t("global.concepts.name"), value: "name" },
        { text: this.$t("global.concepts.current-value"), value: "oldValue" },
        { text: this.$t("global.concepts.new-value"), value: "newValue" },
      ],

      busy: true,
    };
  },

  methods: {
    tableHeight,

    onImport() {
      this.showImport = true;
    },

    async onImportConfirm() {
      this.uploading = true;
      const formData = new FormData();

      formData.append("file", this.file);

      const overwritesChecked = this.overwrites?.length > 0;
      if (!overwritesChecked) {
        this.overwrites = await postForm(
          "/environment-property/check-import",
          formData
        ).catch((e) => {
          this.$store.commit("showError", e);
        });
      }

      if (this.overwrites?.length === 0 || overwritesChecked) {
        await postForm("/environment-property/import", formData)
          .then(() => {
            this.showImport = false;
            this.updateTable();
            this.file = undefined;
            this.overwrites = [];
          })
          .catch((e) => {
            this.$store.commit("showError", e);
            this.file = undefined;
            this.overwrites = [];
          });
      }
      this.uploading = false;
    },

    onCancel() {
      this.showImport = false;
      this.overwrites = [];
      this.file = undefined;
    },

    async getProperties() {
      return await get("/environment-property").catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    async deleteProperty() {
      await remove("/environment-property/" + this.deletingProperty.id)
        .then(() => {
          this.updateTable();
        })
        .catch((error) => {
          this.$store.commit("showError", error);
        });
      this.showDeleteConfirm = false;
    },

    deleteConfirm(row) {
      this.deletingProperty = row;
      this.showDeleteConfirm = true;
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
    },

    async updateTable() {
      this.busy = true;
      this.items = [];
      this.props = await this.getProperties();

      if (this.props) {
        this.props.forEach((value) => {
          const item = {};

          item["id"] = value.id;
          item[this.headers[0].value] = value.name;
          item[this.headers[1].value] = value.value;

          this.items.push(item);
        });
      }
      this.busy = false;
    },

    async onAddEditDone() {
      this.showAddEdit = false;

      await this.updateTable();
    },

    async onAddEditCancel() {
      this.showAddEdit = false;
    },

    addProperty() {
      this.isEdit = false;
      this.editingProperty = {};
      this.showAddEdit = true;
    },

    editProperty(row) {
      this.isEdit = true;
      this.editingProperty = row;
      this.showAddEdit = true;
    },
  },

  async mounted() {
    await this.updateTable();
  },
};
</script>
